import React, { useState } from "react";
import { useAction } from "wasp/client/operations";
import { createRequestOffer } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate } from "react-router-dom";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  ArrowLeft,
  HandHeart,
  DollarSign,
  Tag,
  Phone,
  Save,
  AlertCircle,
  HelpCircle,
  Package,
  Star,
  Gift,
  Mail,
} from "lucide-react";
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
} from "../../components/layout";

interface RequestOfferFormData {
  title: string;
  description: string;
  price: string;
  category: "REQUEST" | "OFFER";
  condition: "NEW" | "LIKE_NEW" | "GOOD" | "FAIR" | "POOR";
  phoneNumber: string;
  email: string;
  visibility: "VERIFIED" | "PENDING" | "REJECTED";
}

const REQUEST_OFFER_CATEGORIES = [
  {
    value: "OFFER",
    label: "Offer",
    icon: Gift,
    description: "I want to offer something",
  },
  {
    value: "REQUEST",
    label: "Request",
    icon: HandHeart,
    description: "I want to request something",
  },
];

const CONDITION_OPTIONS = [
  { value: "NEW", label: "New", description: "Brand new, never used" },
  {
    value: "LIKE_NEW",
    label: "Like New",
    description: "Excellent condition, barely used",
  },
  { value: "GOOD", label: "Good", description: "Good condition, some wear" },
  {
    value: "FAIR",
    label: "Fair",
    description: "Fair condition, noticeable wear",
  },
  {
    value: "POOR",
    label: "Poor",
    description: "Poor condition, significant wear",
  },
];



// Tooltip component
const Tooltip = ({
  children,
  content,
}: {
  children: React.ReactNode;
  content: string;
}) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

export default function AddRequestOfferPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();

  const createRequestOfferFn = useAction(createRequestOffer);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<RequestOfferFormData>({
    title: "",
    description: "",
    price: "",
    category: "OFFER",
    condition: "GOOD",
    phoneNumber: "",
    email: "",
    visibility: "VERIFIED",
  });

  // Pre-fill user contact info
  React.useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        email: user.email || "",
        phoneNumber: user.phoneNumber || "",
      }));
    }
  }, [user]);

  const handleChange = (field: keyof RequestOfferFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!selectedLocation?.id) {
      newErrors.location = "No location selected";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await createRequestOfferFn({
        title: formData.title.trim(),
        description: formData.description.trim(),
        locationId: selectedLocation!.id,
        price: formData.price ? parseFloat(formData.price) : undefined,
        category: formData.category,
        condition: formData.condition,
        phoneNumber: formData.phoneNumber.trim(),
        email: formData.email.trim(),
        visibility: formData.visibility,
      });

      // Navigate back to the request-offer page
      handleGoBack();
    } catch (error) {
      console.error("Error creating request/offer item:", error);
      setErrors({ submit: "Failed to create item. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/request-offer`);
    } else {
      navigate("/request-offer");
    }
  };

  const actionsContent = (
    <ActionButtons>
      <ActionButton
        type="submit"
        form="request-offer-form"
        disabled={isSubmitting || !formData.title.trim()}
        icon={Save}
      >
        {isSubmitting ? "Posting..." : "Submit"}
      </ActionButton>
      <ActionButtonSecondary
        onClick={handleGoBack}
        disabled={isSubmitting}
      >
        Cancel
      </ActionButtonSecondary>
    </ActionButtons>
  );

  const sidebarContent = (
    <SidebarSection title="Tips for Better Results" hideOnMobile>
      <div className="space-y-3 text-sm text-gray-600">
        <div className="flex items-start gap-2">
          <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
          <span>Use clear, descriptive titles that include key details</span>
        </div>
        <div className="flex items-start gap-2">
          <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
          <span>Include detailed descriptions of what you need or are offering</span>
        </div>
        <div className="flex items-start gap-2">
          <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
          <span>Set a fair price or mention if it's free/negotiable</span>
        </div>
        <div className="flex items-start gap-2">
          <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
          <span>Provide accurate contact information for easy communication</span>
        </div>
        <div className="flex items-start gap-2">
          <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
          <span>Be responsive to inquiries to increase your chances of success</span>
        </div>
      </div>
    </SidebarSection>
  );

  if (!user) {
    return (
      <IndividualEditPageLayout title="Access Denied" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600">You must be logged in to post request/offer items.</p>
        </div>
      </IndividualEditPageLayout>
    );
  }

  return (
    <IndividualEditPageLayout
      title="Post Request/Offer Item"
      subtitle={`Location: ${selectedLocation?.name || "No location selected"}`}
      icon={HandHeart}
      actions={actionsContent}
      sidebar={sidebarContent}
    >
      <form id="request-offer-form" onSubmit={handleSubmit} className="space-y-8">
        {/* Request/Offer Type Selection */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6">
          <h3 className="text-lg font-medium text-gray-900">What are you posting?</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {REQUEST_OFFER_CATEGORIES.map((category) => {
              const IconComponent = category.icon;
              return (
                <label
                  key={category.value}
                  className={`relative flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    formData.category === category.value
                      ? "border-blue-500 bg-blue-50"
                      : "border-slate-100 hover:border-slate-200 bg-slate-50/50"
                  }`}
                >
                  <input
                    type="radio"
                    name="category"
                    value={category.value}
                    checked={formData.category === category.value}
                    onChange={(e) =>
                      handleChange(
                        "category",
                        e.target.value as "REQUEST" | "OFFER"
                      )
                    }
                    className="sr-only"
                  />
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${
                      formData.category === category.value ? "bg-blue-100 text-blue-600" : "bg-white text-slate-400"
                    }`}>
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="font-bold text-slate-900">{category.label}</div>
                      <div className="text-sm text-slate-500">{category.description}</div>
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Item Details */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Tag className="w-5 h-5 text-blue-600" /> Item Details
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                className={`w-full px-4 py-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium ${
                  errors.title ? "border-red-500 bg-red-50" : "border-slate-200"
                }`}
                placeholder="e.g., Looking for a laptop, Offering tutoring services"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" /> {errors.title}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                rows={4}
                className={`w-full px-4 py-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${
                  errors.description ? "border-red-500 bg-red-50" : "border-slate-200"
                }`}
                placeholder="Provide detailed information about what you're requesting or offering..."
              />
              {errors.description && (
                <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" /> {errors.description}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Pricing and Attributes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6 text-foreground">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2 px-1">
              <Package className="w-5 h-5 text-orange-600" /> Condition & Price
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Condition</label>
                <select
                  value={formData.condition}
                  onChange={(e) =>
                    handleChange(
                      "condition",
                      e.target.value as "NEW" | "LIKE_NEW" | "GOOD" | "FAIR" | "POOR"
                    )
                  }
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all"
                >
                  {CONDITION_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Price ($)</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => handleChange("price", e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-emerald-500/20 transition-all font-semibold"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6 text-foreground">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2 px-1">
              <Mail className="w-5 h-5 text-blue-600" /> Contact Info
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Phone</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => handleChange("phoneNumber", e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all"
                    placeholder="Phone number"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {errors.submit && (
          <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm font-medium">
            {errors.submit}
          </div>
        )}
      </form>
    </IndividualEditPageLayout>
  );
}
